// src/pages/QRMallKioskPage.js
// Mobile multi-vendor kiosk for table QR ordering
// FIX: creates real DB orders BEFORE payment modal
// Payment uses the first real order ID

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage }  from '../context/LanguageContext';
import api              from '../services/api';
import PaymentModal     from '../components/PaymentModal';
import ModifierSelector from '../components/ModifierSelector';
import CashGateModal    from '../components/CashGateModal';
import LangToggle       from '../components/LangToggle';
import { applyTheme }           from '../utils/applyTheme';
import { calculateServiceFee }  from '../utils/serviceFeeCalculator';
import { applyDietary, categoriesOf } from '../utils/dietary';
import DietarySheet from '../components/DietarySheet';
import { DietaryPill, DietaryHiddenNote, UnverifiedTag } from '../components/DietaryFilterUI';

export default function QRMallKioskPage() {
  const { isAr } = useLanguage();

  const slug    = window.location.pathname.split('/order/mall/')[1]?.split('?')[0]?.split('/')[0] || null;
  const tableNo = new URLSearchParams(window.location.search).get('table') || null;

  const [mall,         setMall]         = useState(null);
  const [vendors,      setVendors]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [carts,        setCarts]        = useState({});
  const [activeVendor, setActiveVendor] = useState(null);
  const [view,         setView]         = useState('stalls');
  const [modifierItem, setModifierItem] = useState(null);

  // Payment state
  const [placing,      setPlacing]      = useState(false);  // creating orders
  const [placeError,   setPlaceError]   = useState('');
  const [payOrder,     setPayOrder]     = useState(null);   // first real order for payment
  const [allOrderIds,  setAllOrderIds]  = useState([]);     // all created order IDs
  const [showPayment,  setShowPayment]  = useState(false);
  const [showCashGate, setShowCashGate] = useState(false);
  const [ordered,      setOrdered]      = useState(false);
  const [orderedCashPending, setOrderedCashPending] = useState(false);
  const [orderNos,     setOrderNos]     = useState([]);
  const [diet,          setDiet]         = useState('none');   // session-only dietary preference
  const [showDietSheet, setShowDietSheet] = useState(true);

  useEffect(() => {
    if (!slug) { setError('Invalid QR code.'); setLoading(false); return; }
    api.get(`/public/mall/${slug}/vendors`, { params: tableNo ? { table: tableNo } : undefined })
      .then(r => {
        setMall(r.data.mall);
        setVendors(r.data.vendors || []);
        applyTheme(r.data.mall?.kioskTheme || 'dark', r.data.mall?.brandColor, r.data.mall?.bgColor, r.data.mall?.tileColor, r.data.mall?.fontColor, r.data.mall?.buttonTextColor, r.data.mall?.surfaceColor);
      })
      .catch(err => setError(err.response?.data?.error || 'Could not load menu.'))
      .finally(() => setLoading(false));
    return () => applyTheme('dark');
  }, [slug]);

  // Cart helpers
  const getVendorCart  = useCallback((vid) => carts[vid] || [], [carts]);
  const getVendorTotal = useCallback((vid) => getVendorCart(vid).reduce((s,i) => s + i.price * i.qty, 0), [getVendorCart]);
  const getVendorCount = useCallback((vid) => getVendorCart(vid).reduce((s,i) => s + i.qty, 0), [getVendorCart]);

  const grandTotal      = vendors.reduce((s,vd) => s + getVendorTotal(vd.vendor.id), 0);
  const grandTotalVAT   = parseFloat((grandTotal * 1.05).toFixed(2));
  const totalItems      = vendors.reduce((s,vd) => s + getVendorCount(vd.vendor.id), 0);
  const stallsWithItems = vendors.filter(vd => getVendorCount(vd.vendor.id) > 0);

  // Service fee — preview from mall config (server-side is authoritative)
  const mallFeeConfig = mall?.serviceFeeConfig
    ? (typeof mall.serviceFeeConfig === 'string' ? (() => { try { return JSON.parse(mall.serviceFeeConfig); } catch(_) { return null; } })() : mall.serviceFeeConfig)
    : null;
  const serviceFee    = calculateServiceFee(grandTotal, mallFeeConfig);
  const payTotal      = parseFloat((grandTotalVAT + serviceFee.amount).toFixed(2));

  const addToCart = useCallback((vendorId, item) => {
    setCarts(prev => {
      const cart     = [...(prev[vendorId] || [])];
      const existing = cart.findIndex(i => i.id === item.id && i.modifierNote === item.modifierNote);
      if (existing >= 0) cart[existing] = { ...cart[existing], qty: cart[existing].qty + 1 };
      else               cart.push({ ...item, qty: 1 });
      return { ...prev, [vendorId]: cart };
    });
  }, []);

  const setQty = useCallback((vendorId, itemId, qty) => {
    setCarts(prev => ({
      ...prev,
      [vendorId]: (prev[vendorId] || []).map(i => i.id === itemId ? { ...i, qty } : i).filter(i => i.qty > 0),
    }));
  }, []);

  const removeFromCart = useCallback((vendorId, itemId) => {
    setCarts(prev => ({ ...prev, [vendorId]: (prev[vendorId] || []).filter(i => i.id !== itemId) }));
  }, []);

  const getQty = (vendorId, itemId) => (carts[vendorId] || []).find(i => i.id === itemId)?.qty || 0;

  // ── Step 1: Create all orders via mall-checkout endpoint ─
  const handleCheckout = async () => {
    if (totalItems === 0 || placing) return;
    setPlacing(true);
    setPlaceError('');

    try {
      const res = await api.post('/orders/mall-checkout', {
        mallId:     mall.id,
        tableNo:    tableNo || undefined,
        sourceKiosk: 'TABLE_QR',
        vendors: stallsWithItems.map(vd => {
          const cart = getVendorCart(vd.vendor.id);
          return {
            vendorId: vd.vendor.id,
            items:    cart.map(i => ({ menuItemId: i.id, quantity: i.qty })),
            notes:    cart.filter(i => i.modifierNote).map(i => `${i.name}: ${i.modifierNote}`).join(' | ') || undefined,
          };
        }),
      });

      const { orders, transactionGroupId, serviceFee: serverFee, paymentTotal } = res.data;
      const firstOrder = orders[0];
      const allIds     = orders.map(o => o.id);

      const orderNosData = orders.map((o, idx) => ({
        orderId:     o.id,
        orderNumber: o.orderNumber,
        total:       o.total,
        vendor:      stallsWithItems[idx]?.vendor?.name || '',
        emoji:       stallsWithItems[idx]?.vendor?.emoji || '🍽',
      }));

      const combinedForPayment = {
        id:                 firstOrder.id,
        orderNumber:        firstOrder.orderNumber,
        transactionGroupId,
        total:              paymentTotal,    // sum(order.total) + serviceFee from server
        subtotal:           grandTotal,
        tax:                parseFloat((grandTotal * 0.05).toFixed(2)),
        serviceFeeAmount:   serverFee?.amount || 0,
        serviceFeeLabel:    serverFee?.label  || 'Service Fee',
        tableNo,
        sourceKiosk: 'TABLE_QR',
        items: stallsWithItems.flatMap(vd =>
          getVendorCart(vd.vendor.id).map(i => ({
            name:     `${vd.vendor.emoji} ${i.name}`,
            quantity: i.qty,
            price:    i.price,
          }))
        ),
      };

      setPayOrder(combinedForPayment);
      setAllOrderIds(allIds);
      setOrderNos(orderNosData);
      setShowPayment(true);

    } catch (err) {
      setPlaceError(err.response?.data?.error || err.message || 'Could not place orders. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  // ── Step 2: After payment confirmed, show success ─────────
  const handlePaymentSuccess = ({ cashPending } = {}) => {
    setShowPayment(false);
    setCarts({});
    setOrdered(true);
    setOrderedCashPending(!!cashPending);
    setView('stalls');
  };

  // ── Success screen ────────────────────────────────────────
  if (ordered) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, textAlign:'center' }}>
      <div style={{ fontSize:64, marginBottom:16 }}>{orderedCashPending ? '🎫' : '✅'}</div>
      <div style={{ fontSize:22, fontWeight:800, color: orderedCashPending ? 'var(--accent)' : 'var(--green)', marginBottom:6 }}>
        {orderedCashPending ? 'Pay at the Counter' : 'Orders Placed!'}
      </div>
      {tableNo && <div style={{ fontSize:14, color:'var(--accent)', marginBottom:16 }}>🪑 Table {tableNo}</div>}
      {orderedCashPending && (
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16, maxWidth:340 }}>
          Pay cash at the counter for each order below. Preparation starts once payment is received.
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:10, width:'100%', maxWidth:340, marginBottom:24 }}>
        {orderNos.map((o, i) => (
          <div key={i} style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:14 }}>{o.emoji} {o.vendor}</span>
            <span style={{ fontSize:15, fontWeight:700, color:'var(--accent)' }}>{o.orderNumber}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:20 }}>
        {orderedCashPending ? 'Pay at counter to start preparation' : 'Your orders are being prepared 🍽'}
      </div>
      <button className="btn btn-ghost" onClick={() => { setOrdered(false); setOrderNos([]); setOrderedCashPending(false); }}>
        Order More →
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'var(--text2)', fontSize:14 }}>Loading menu...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24, textAlign:'center' }}>
      <div>
        <div style={{ fontSize:48, marginBottom:16 }}>😕</div>
        <div style={{ fontSize:18, fontWeight:700, color:'var(--red)', marginBottom:8 }}>Unavailable</div>
        <div style={{ fontSize:14, color:'var(--text2)' }}>{error}</div>
      </div>
    </div>
  );

  const currentVendorData = vendors.find(vd => vd.vendor.id === activeVendor);

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', paddingBottom: totalItems > 0 ? 90 : 20 }}>

      {showDietSheet && (
        <DietarySheet isAr={isAr} initial={diet}
          onContinue={(p) => { setDiet(p || 'none'); setShowDietSheet(false); }}
          onSkip={() => setShowDietSheet(false)} />
      )}

      {/* Header */}
      <div style={{ background:'var(--bg2)', borderBottom:'1px solid var(--border)', padding:'14px 16px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            {view === 'menu' && currentVendorData ? (
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <button onClick={() => setView('stalls')} style={{ background:'none', border:'none', color:'var(--text2)', fontSize:18, cursor:'pointer', padding:'0 4px' }}>←</button>
                <div>
                  <div style={{ fontSize:16, fontWeight:700 }}>{currentVendorData.vendor.emoji} {currentVendorData.vendor.name}</div>
                  {tableNo && <div style={{ fontSize:11, color:'var(--accent)' }}>🪑 Table {tableNo}</div>}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:17, fontWeight:700 }}>🏬 {mall?.name}</div>
                {tableNo
                  ? <div style={{ fontSize:12, color:'var(--accent)', marginTop:2 }}>🪑 Table {tableNo} — Order from any stall</div>
                  : <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>Order from any stall</div>
                }
              </div>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <LangToggle />
            {totalItems > 0 && view !== 'checkout' && (
              <button onClick={() => setView('checkout')} style={{ padding:'6px 12px', borderRadius:8, background:'var(--accent)', border:'none', color:'var(--button-text)', fontWeight:700, fontSize:12, cursor:'pointer' }}>
                🛒 {totalItems}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── STALLS VIEW ── */}
      {view === 'stalls' && (
        <div style={{ padding:'12px 16px' }}>
          <div style={{ fontSize:13, color:'var(--text2)', marginBottom:14 }}>
            {vendors.length} stall{vendors.length !== 1 ? 's' : ''} · tap to browse
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {vendors.map(vd => {
              const count  = getVendorCount(vd.vendor.id);
              const vtotal = getVendorTotal(vd.vendor.id);
              return (
                <div key={vd.vendor.id}
                  onClick={() => { setActiveVendor(vd.vendor.id); setView('menu'); }}
                  style={{ background:'var(--bg2)', borderRadius:14, padding:16, cursor:'pointer', border:`1px solid ${count>0?'var(--accent)':'var(--border)'}`, transition:'all .15s' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                    <span style={{ fontSize:42 }}>{vd.vendor.emoji}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:16 }}>{vd.vendor.name}</div>
                      <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>
                        {vd.menuItems?.length || 0} items{vd.stallNo ? ` · Stall ${vd.stallNo}` : ''}
                      </div>
                    </div>
                    {count > 0 ? (
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:15, fontWeight:700, color:'var(--accent)' }}>AED {vtotal.toFixed(2)}</div>
                        <div style={{ fontSize:11, color:'var(--text2)' }}>{count} item{count>1?'s':''}</div>
                      </div>
                    ) : (
                      <span style={{ color:'var(--text3)', fontSize:18 }}>→</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MENU VIEW ── */}
      {view === 'menu' && currentVendorData && (
        <MenuView
          vendorData={currentVendorData}
          getQty={(id) => getQty(currentVendorData.vendor.id, id)}
          onAdd={(item) => addToCart(currentVendorData.vendor.id, item)}
          onSetQty={(id, qty) => setQty(currentVendorData.vendor.id, id, qty)}
          onModifier={setModifierItem}
          isAr={isAr}
          diet={diet}
          onClearDiet={() => setDiet('none')}
        />
      )}

      {/* ── CHECKOUT VIEW ── */}
      {view === 'checkout' && (
        <div style={{ padding:'12px 16px' }}>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>🛒 Your Order</div>

          {tableNo && (
            <div style={{ padding:'8px 14px', background:'#1a2d3d', borderRadius:8, marginBottom:12, fontSize:13, color:'var(--blue)', fontWeight:600 }}>
              🪑 Table {tableNo}
            </div>
          )}

          {placeError && (
            <div style={{ padding:'10px 14px', background:'#3d1a1a', borderRadius:8, marginBottom:12, fontSize:13, color:'var(--red)', border:'1px solid var(--red)' }}>
              ❌ {placeError}
            </div>
          )}

          {stallsWithItems.map(vd => (
            <div key={vd.vendor.id} style={{ marginBottom:16 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:8, color:'var(--text2)' }}>
                {vd.vendor.emoji} {vd.vendor.name}
              </div>
              {getVendorCart(vd.vendor.id).map(item => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500 }}>{item.name}</div>
                    {item.modifierNote && <div style={{ fontSize:11, color:'var(--text3)' }}>{item.modifierNote}</div>}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <button onClick={() => setQty(vd.vendor.id, item.id, item.qty - 1)}
                        style={{ width:26, height:26, borderRadius:'50%', background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text)', cursor:'pointer' }}>−</button>
                      <span style={{ fontSize:13, fontWeight:600, minWidth:16, textAlign:'center' }}>{item.qty}</span>
                      <button onClick={() => setQty(vd.vendor.id, item.id, item.qty + 1)}
                        style={{ width:26, height:26, borderRadius:'50%', background:'var(--accent)', border:'none', color:'var(--button-text)', cursor:'pointer' }}>+</button>
                    </div>
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--accent)', minWidth:60, textAlign:'right' }}>
                      AED {(item.price * item.qty).toFixed(2)}
                    </span>
                    <button onClick={() => removeFromCart(vd.vendor.id, item.id)}
                      style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:16 }}>✕</button>
                  </div>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text2)', marginTop:6 }}>
                <span>Subtotal</span><span>AED {getVendorTotal(vd.vendor.id).toFixed(2)}</span>
              </div>
            </div>
          ))}

          {/* Grand total */}
          <div style={{ borderTop:'2px solid var(--border)', paddingTop:12, marginTop:4 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:4 }}>
              <span>Subtotal</span><span>AED {grandTotal.toFixed(2)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:4 }}>
              <span>VAT (5%)</span><span>AED {(grandTotal * 0.05).toFixed(2)}</span>
            </div>
            {serviceFee.amount > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'var(--text2)', marginBottom:4 }}>
                <span>{serviceFee.label}</span><span>AED {serviceFee.amount.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:18, marginTop:6 }}>
              <span>Total</span>
              <span style={{ color:'var(--accent)' }}>AED {payTotal.toFixed(2)}</span>
            </div>
          </div>

          {placeError && (
            <div style={{ marginTop:10, padding:'10px 14px', background:'#3d1a1a', borderRadius:8, color:'var(--red)', fontSize:13, border:'1px solid var(--red)' }}>
              ❌ {placeError}
            </div>
          )}

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button className="btn btn-ghost" style={{ flex:1 }} onClick={() => setView('stalls')}>← Add More</button>
            <button className="btn btn-primary" style={{ flex:2, padding:13, fontSize:15 }}
              disabled={placing}
              onClick={handleCheckout}>
              {placing ? '⏳ Placing orders...' : `Pay AED ${payTotal.toFixed(2)} →`}
            </button>
          </div>
        </div>
      )}

      {/* Sticky bottom bar */}
      {totalItems > 0 && view !== 'checkout' && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--bg2)', borderTop:'1px solid var(--border)', padding:'12px 16px', zIndex:200, display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, color:'var(--text2)' }}>
              {totalItems} item{totalItems>1?'s':''} from {stallsWithItems.length} stall{stallsWithItems.length>1?'s':''}
            </div>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--accent)' }}>AED {payTotal.toFixed(2)}</div>
          </div>
          <button className="btn btn-primary" style={{ padding:'12px 20px', fontSize:14, fontWeight:700 }}
            onClick={() => setView('checkout')}>
            Review Order →
          </button>
        </div>
      )}

      {/* Modifier selector */}
      {modifierItem && (
        <ModifierSelector
          item={modifierItem}
          onAdd={(item) => { addToCart(activeVendor, item); setModifierItem(null); }}
          onClose={() => setModifierItem(null)}
        />
      )}

      {/* Payment modal — uses REAL order ID */}
      {showPayment && payOrder && (
        <PaymentModal
          order={payOrder}
          paymentConfig={mall?.paymentConfig}
          sourceKiosk="TABLE_QR"
          onSuccess={handlePaymentSuccess}
          onClose={() => {
            setShowPayment(false);
            // Orders were already created — they sit as AWAITING_PAYMENT
            // Staff can cancel from Orders tab if customer abandons
          }}
        />
      )}

      {/* Cash gate */}
      {showCashGate && (
        <CashGateModal
          orderTotal={grandTotalVAT.toFixed(2)}
          onConfirm={() => { setShowCashGate(false); handlePaymentSuccess(); }}
          onCancel={() => setShowCashGate(false)}
        />
      )}
    </div>
  );
}

// ── Menu view ─────────────────────────────────────────────────
function MenuView({ vendorData, getQty, onAdd, onSetQty, onModifier, isAr, diet = 'none', onClearDiet }) {
  const [cat,    setCat]    = useState('All');
  const [search, setSearch] = useState('');
  const [showUnverified, setShowUnverified] = useState(false);

  const { shown: items, unverified } = applyDietary(vendorData.menuItems || [], diet, showUnverified);
  const cats      = categoriesOf(items);
  const activeCat = cats.includes(cat) ? cat : 'All';

  const filtered = items
    .filter(i => activeCat === 'All' || i.category === activeCat)
    .filter(i => i.name?.toLowerCase().includes(search.toLowerCase()));

  const handleTap = async (item) => {
    try {
      const r = await api.get(`/menu/${item.id}/modifiers`);
      if (r.data?.length > 0) { onModifier(item); return; }
    } catch (_) {}
    onAdd(item);
  };

  return (
    <div>
      <div style={{ padding:'10px 16px', background:'var(--bg2)', borderBottom:'1px solid var(--border)' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search..." style={{ width:'100%', padding:'8px 12px', borderRadius:8, fontSize:13 }} />
      </div>
      {diet !== 'none' && (
        <div style={{ padding:'10px 16px 0' }}>
          <DietaryPill preference={diet} isAr={isAr} onClear={() => { onClearDiet?.(); setShowUnverified(false); }} />
        </div>
      )}
      <div style={{ display:'flex', gap:8, padding:'10px 16px', overflowX:'auto', background:'var(--bg2)', borderBottom:'1px solid var(--border)' }}>
        {cats.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            padding:'5px 12px', borderRadius:20, fontSize:12, whiteSpace:'nowrap', cursor:'pointer', border:'none',
            background: activeCat===c ? 'var(--accent)' : 'var(--bg3)',
            color:      activeCat===c ? '#000' : 'var(--text2)',
            fontWeight: activeCat===c ? 700 : 400,
          }}>{c}</button>
        ))}
      </div>
      <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.map(item => {
          const qty = getQty(item.id);
          return (
            <div key={item.id} style={{
              background:'var(--bg2)', borderRadius:12, padding:12,
              border:`1px solid ${qty>0?'var(--accent)':'var(--border)'}`,
              display:'flex', alignItems:'center', gap:12,
            }}>
              {item.imageUrl
                ? <img src={item.imageUrl} alt="" style={{ width:60, height:60, borderRadius:8, objectFit:'cover', flexShrink:0 }} onError={e=>e.target.style.display='none'} />
                : <div style={{ width:60, height:60, borderRadius:8, background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, flexShrink:0 }}>{item.emoji}</div>
              }
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{item.name}{item.unverified && <UnverifiedTag isAr={isAr} />}</div>
                <div style={{ fontSize:15, fontWeight:700, color:'var(--accent)', marginTop:4 }}>AED {item.price}</div>
              </div>
              <div style={{ flexShrink:0 }}>
                {qty > 0 ? (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <button onClick={() => onSetQty(item.id, qty-1)} style={{ width:28, height:28, borderRadius:'50%', background:'var(--bg4)', border:'1px solid var(--border)', color:'var(--text)', cursor:'pointer' }}>−</button>
                    <span style={{ fontWeight:700, minWidth:18, textAlign:'center' }}>{qty}</span>
                    <button onClick={() => handleTap(item)} style={{ width:28, height:28, borderRadius:'50%', background:'var(--accent)', border:'none', color:'var(--button-text)', cursor:'pointer' }}>+</button>
                  </div>
                ) : (
                  <button onClick={() => handleTap(item)} style={{ padding:'7px 14px', borderRadius:8, background:'var(--accent)', border:'none', color:'var(--button-text)', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                    Add
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>No items found</div>
        )}
        {diet !== 'none' && (
          <DietaryHiddenNote count={unverified.length} expanded={showUnverified} isAr={isAr} onToggle={() => setShowUnverified(v => !v)} />
        )}
      </div>
    </div>
  );
}
